import React, { useState, useContext, createContext, useEffect } from 'react';
import { fetchCurrency } from '../controllers/CurrencyConnectionLayer';
import 'flag-icons/css/flag-icons.min.css';
const CurrencyContext = createContext();

const STORED_CURRENCY_KEY = 'selectedCurrency';
const ZERO_DECIMAL_CURRENCIES = new Set(['JPY']);

export const CurrencyProvider = ({ children }) => {
    const [selectedCurrency, setSelectedCurrency] = useState(() => localStorage.getItem(STORED_CURRENCY_KEY) || 'CAD');
    const [currentRate, setCurrentRate] = useState(1);
    const [currentSymbol, setCurrentSymbol] = useState('$');
    const [currentDecimals, setCurrentDecimals] = useState(true);

    const [currencyRates, setCurrencyRates] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        selectCurrency(selectedCurrency);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectCurrency = async (currency) => {
        setSelectedCurrency(currency.toUpperCase());
        localStorage.setItem(STORED_CURRENCY_KEY, currency.toUpperCase());

        let rates = currencyRates;

        if (!lastUpdated || (Date.now() - lastUpdated > 3600000)) {
            rates = await fetchCurrencyRates();

            setCurrencyRates(rates);
            setLastUpdated(Date.now());
        }

        const rate = rates.find(rate => rate.code === currency.toUpperCase());

        if (rate) {
            setCurrentRate(rate.rate);
            setCurrentSymbol(rate.symbol);
            setCurrentDecimals(rate.decimals);
        }
    }

    const fetchCurrencyRates = async () => {
        return await fetchCurrency();
    }

    const handleCurrencyChange = (selectedOption) => {
      if (selectedOption) {
        localStorage.setItem(STORED_CURRENCY_KEY, selectedOption.value.toUpperCase());
        window.location.reload();
      }
    }

    const currencySymbol = currentSymbol || '$';

    const getCurrencySymbolByCode = (currencyCode) => {
        const normalizedCode = (currencyCode || '').toUpperCase();
        const rate = currencyRates.find((rate) => rate.code === normalizedCode);

        if (rate?.symbol) {
            return rate.symbol;
        }

        return currentSymbol || '$';
    };

    const getCurrencyDecimalsByCode = (currencyCode) => {
        const normalizedCode = (currencyCode || '').toUpperCase();
        const rate = currencyRates.find((rate) => rate.code === normalizedCode);

        if (typeof rate?.decimals === 'boolean') {
            return rate.decimals;
        }

        return !ZERO_DECIMAL_CURRENCIES.has(normalizedCode);
    };

    const formatPrice = (price, currencyCode = selectedCurrency) => {
        const numericPrice = parseFloat(price) || 0;
        const normalizedCurrencyCode = (currencyCode || selectedCurrency || '').toUpperCase();
        const usesDecimals = getCurrencyDecimalsByCode(normalizedCurrencyCode);

        if (!usesDecimals) {
            return Math.round(numericPrice);
        }

        return numericPrice.toFixed(2);
    }

    const roundForCurrency = (amount, currencyCode) => {
        if (ZERO_DECIMAL_CURRENCIES.has(currencyCode)) {
            return Math.round(amount / 25) * 25;
        }
        return Math.round(amount * 4) / 4;
    }

    // For prices stored in CAD (e.g. cart items) that still need converting to the selected currency before formatting.
    const formatCadPrice = (cadPrice, addConversionFee = true) => {
        const conversionFee = addConversionFee ? 1.02 : 1;
        const numericCadPrice = parseFloat(cadPrice) || 0;
        const convertedAmount = numericCadPrice * conversionFee * currentRate;
        return formatPrice(convertedAmount);
    }

    // Sums an array of CAD amounts (e.g. a price breakdown) and converts+formats the total as one price.
    const calculateConvertedTotalPrice = (cadPriceArray) => {
        var convertedTotal = 0
        var multiplier = selectedCurrency === "CAD" ? 1 : 1.02;
        for (let i = 0; i < cadPriceArray.length; i++) {
            convertedTotal += cadPriceArray[i] * multiplier * currentRate;
        }

        return roundForCurrency(convertedTotal, selectedCurrency);
    }

    // ISO 4217 currency codes are conventionally built from the ISO 3166-1 country code
    // plus one letter (CAD -> CA, JPY -> JP, EUR -> EU), which is also what flag-icons keys on.
    const getFlagCountryCode = (currencyCode = '') => {
        return currencyCode.slice(0, 2).toLowerCase();
    }

    const currencyOptions = currencyRates.map(rate => ({
        value: rate.code.toLowerCase(),
        label: <div className="currency-option">{rate.code} <span className={`fi fi-${getFlagCountryCode(rate.code)} currency-flag`}></span></div>,
    }));

    return (
        <CurrencyContext.Provider value={{ selectedCurrency, currentRate, currencySymbol, currentDecimals, currencyOptions, formatPrice, formatCadPrice, calculateConvertedTotalPrice, selectCurrency, handleCurrencyChange, getCurrencySymbolByCode, getCurrencyDecimalsByCode }}>
            {children}
        </CurrencyContext.Provider>
    );
};

export const useCurrency = () => {
  return useContext(CurrencyContext);
};
